"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./fireworks.module.css";

const CONFIG = {
    colors: [
        "#ffffff",
        "#ffd54a",
        "#ff5252",
        "#4a90ff",
        "#ff8a3d",
        "#5ce1e6",
    ],

    // Physics
    gravity: 0.045,
    friction: 0.985,

    // Timing (ms)
    displayDuration: 9000,
    fadeDuration: 1200,

    // How often to launch a wave and how many bursts per wave
    launchIntervalMin: 220,
    launchIntervalMax: 520,
    burstsPerLaunchMin: 1,
    burstsPerLaunchMax: 3,

    // Particles per burst
    particleCountMin: 50,
    particleCountMax: 150,

    // Explosion spread
    baseSpeedMin: 3.6,
    baseSpeedMax: 7.2,

    // Per-particle appearance / lifetime
    particleDecayMin: 0.006,
    particleDecayMax: 0.016,
    particleRadiusMin: 1.8,
    particleRadiusMax: 3.6,

    // Where bursts spawn (fraction of viewport)
    spawnXMin: 0.1,
    spawnXMax: 0.9,
    spawnYMin: 0.1,
    spawnYMax: 0.6,
};

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

function createParticles(x, y) {
    const particles = [];
    const count = Math.floor(randomBetween(CONFIG.particleCountMin, CONFIG.particleCountMax));
    const color = CONFIG.colors[Math.floor(Math.random() * CONFIG.colors.length)];
    const baseSpeed = randomBetween(CONFIG.baseSpeedMin, CONFIG.baseSpeedMax);

    for (let i = 0; i < count; i += 1) {
        const angle = (Math.PI * 2 * i) / count + randomBetween(-0.08, 0.08);
        const speed = baseSpeed * randomBetween(0.45, 1);

        particles.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            alpha: 1,
            decay: randomBetween(CONFIG.particleDecayMin, CONFIG.particleDecayMax),
            radius: randomBetween(CONFIG.particleRadiusMin, CONFIG.particleRadiusMax),
            color,
        });
    }

    return particles;
}

export default function Fireworks() {
    const canvasRef = useRef(null);
    const [active, setActive] = useState(true);

    useEffect(() => {
        const prefersReducedMotion =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

        if (prefersReducedMotion) {
            setActive(false);
            return undefined;
        }

        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const context = canvas.getContext("2d");
        let particles = [];
        let animationFrame = 0;
        let launchTimer = 0;
        let running = true;
        const startedAt = performance.now();

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            canvas.width = Math.floor(window.innerWidth * dpr);
            canvas.height = Math.floor(window.innerHeight * dpr);
            canvas.style.width = `${window.innerWidth}px`;
            canvas.style.height = `${window.innerHeight}px`;
            context.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        resize();
        window.addEventListener("resize", resize);

        const launch = () => {
            const bursts = Math.floor(randomBetween(CONFIG.burstsPerLaunchMin, CONFIG.burstsPerLaunchMax));
            for (let b = 0; b < bursts; b += 1) {
                const x = randomBetween(window.innerWidth * CONFIG.spawnXMin, window.innerWidth * CONFIG.spawnXMax);
                const y = randomBetween(window.innerHeight * CONFIG.spawnYMin, window.innerHeight * CONFIG.spawnYMax);
                particles = particles.concat(createParticles(x, y));
            }

            const elapsed = performance.now() - startedAt;
            if (elapsed < CONFIG.displayDuration - 1500) {
                launchTimer = window.setTimeout(launch, randomBetween(CONFIG.launchIntervalMin, CONFIG.launchIntervalMax));
            }
        };

        const render = () => {
            if (!running) return;

            context.clearRect(0, 0, window.innerWidth, window.innerHeight);
            context.globalCompositeOperation = "lighter";

            particles = particles.filter((p) => p.alpha > 0);
            for (const p of particles) {
                p.vx *= CONFIG.friction;
                p.vy *= CONFIG.friction;
                p.vy += CONFIG.gravity;
                p.x += p.vx;
                p.y += p.vy;
                p.alpha -= p.decay;

                context.globalAlpha = Math.max(p.alpha, 0);
                context.fillStyle = p.color;
                context.beginPath();
                context.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                context.fill();
            }

            context.globalAlpha = 1;
            animationFrame = window.requestAnimationFrame(render);
        };

        launch();
        render();

        const stopTimer = window.setTimeout(() => {
            setActive(false);
        }, CONFIG.displayDuration + CONFIG.fadeDuration);

        return () => {
            running = false;
            window.cancelAnimationFrame(animationFrame);
            window.clearTimeout(launchTimer);
            window.clearTimeout(stopTimer);
            window.removeEventListener("resize", resize);
        };
    }, []);

    if (!active) return null;

    return (
        <canvas
            ref={canvasRef}
            className={styles.fireworksCanvas}
            aria-hidden="true"
            style={{ animationDelay: `${CONFIG.displayDuration}ms` }}
        />
    );
}
